import { IconTrendingDown, IconTrendingUp } from '@tabler/icons-react'
import React, { useEffect, useState } from 'react'
import { Card, Col, Dropdown, Row } from 'react-bootstrap'
import { Area, AreaChart, ResponsiveContainer } from 'recharts'
import { GetRequest } from "../../../../Utils/hooks/ApiHandler";
import { useGetEssentials } from "../../../../Utils/hooks/useGetEssentials"
import { putCommaInLeadsValue } from '../../../../Utils/helpers/commonhelper';

const formatGrowthPercent = (current, last) => {
    let percent
    if (last === 0 && current === 0) {
        percent = 0
    } else if (last === 0) {
        percent = current > 0 ? 100 : -100
    } else {
        percent = ((current - last) / last) * 100
    }

    percent = Math.max(-100, Math.min(100, percent))
    const sign = percent > 0 ? '+' : ''
    return `${sign}${percent.toFixed(2)}%`
}

export const CEOStatsCard = () => {
    const { headers, extras, customerId, loginUserId, userType } = useGetEssentials()

    const [revenueData, setRevenueData] = useState({})
    const [dealsWonRate, setDealsWonRate] = useState({})
    const [recurrRevenue, setRecurrRevenue] = useState({})
    const [avgDealSize, setAvgDealSize] = useState({})

    const sparklineData = [
        { val: 12 }, { val: 18 }, { val: 14 }, { val: 22 }, { val: 19 }, { val: 28 }, { val: 24 }
    ];

    // Component state for selecting the timeframe
    const [selectedPeriod, setSelectedPeriod] = useState("today");
    const dividerStyle = {
        width: "100%",
        borderTop: "1px solid #dee2e6",
        margin: "0.75rem 0 0.25rem"
    };
    const revenueDataPeriod = [{ value: 'today', title: "Today" }, { value: 'month', title: "This Month" }, { value: 'quarter', title: "This Quarter" }, { value: 'year', title: "This Year" }]

    // Calculate MoM percentage change
    const calculateMoM = (current, previous) => {
        if (previous === 0) {
            return current > 0 ? 100 : 0; // If previous is 0, any positive amount is 100% increase
        }
        return ((current - previous) / previous) * 100;
    };

    const calculateWinLossRate = (wonCurrMonthDeals, lostCurrMonthDeals, lastMonthWonDeals, lastMonthLostDeals) => {
        const currMonthTotal = wonCurrMonthDeals + lostCurrMonthDeals;
        const currMonthWinRate = currMonthTotal > 0 ? (wonCurrMonthDeals / currMonthTotal) * 100 : 0;
        const currMonthLossRate = currMonthTotal > 0 ? (lostCurrMonthDeals / currMonthTotal) * 100 : 0;

        // Last month rates
        const lastMonthTotal = lastMonthWonDeals + lastMonthLostDeals;
        const lastMonthWinRate = lastMonthTotal > 0 ? (lastMonthWonDeals / lastMonthTotal) * 100 : 0;
        const lastMonthLossRate = lastMonthTotal > 0 ? (lastMonthLostDeals / lastMonthTotal) * 100 : 0;

        // Or percentage change (relative change)
        const winRatePercentChange = lastMonthWinRate !== 0
            ? ((currMonthWinRate - lastMonthWinRate) / lastMonthWinRate) * 100
            : (currMonthWinRate > 0 ? 100 : 0);

        const lossRatePercentChange = lastMonthLossRate !== 0
            ? ((currMonthLossRate - lastMonthLossRate) / lastMonthLossRate) * 100
            : (currMonthLossRate > 0 ? 100 : 0);
        return { winRatePercentChange, lossRatePercentChange }
    }

    const fetchRevenueData = async () => {
        try {
            const res = await GetRequest(`dashboard/revenue/${customerId}/${loginUserId}/${userType}/${selectedPeriod}`, { headers }, extras)
            if (res.status === "fail" || res.success === false) {
                setRevenueData(res.data.map(item => ({
                    value: item.period,
                    title: selectedPeriod === "today" ? "Today's Revenue" : selectedPeriod === "month" ? "This Month" : selectedPeriod === "quarter" ? "This Quarter" : "This Year",
                    current: 0,
                    prevLabel: selectedPeriod === "today" ? "Yesterday" : selectedPeriod === "month" ? "Last Month" : selectedPeriod === "quarter" ? "Last Quarter" : "Last Year",
                    prev: 0,
                    growth: 0,
                    isUp: true
                })))
                return
            }
            const current = Number(res.data?.['current']?.[0]?.['total_revenue']) || 0
            const last = Number(res.data?.['last']?.[0]?.['total_revenue']) || 0

            setRevenueData({
                value: selectedPeriod,
                title: selectedPeriod === "today" ? "Today's Revenue" : selectedPeriod === "month" ? "This Month" : selectedPeriod === "quarter" ? "This Quarter" : "This Year",
                current: current,
                prevLabel: selectedPeriod === "today" ? "Yesterday" : selectedPeriod === "month" ? "Last Month" : selectedPeriod === "quarter" ? "Last Quarter" : "Last Year",
                prev: last,
                growth: formatGrowthPercent(current, last),
                isUp: current >= last
            })

        } catch (error) {
            console.error(error)
            return
        }
    }

    const fetchCardsData = async () => {
        try {
            const res = await GetRequest(`dashboard/ceo_cards/${customerId}`, { headers }, extras)
            if (res.status === 'fail' || res.success === false) {
                return
            }

            const dealSize = res.data.avg_deal[0].avg_total_amount

            const currentMonthAmount = Number(res.data?.total_deal_current_mom[0]?.total_amount || 0);
            const lastMonthAmount = Number(res.data?.total_deal_last_mom[0]?.total_amount || 0);
            const momPercentage = calculateMoM(currentMonthAmount, lastMonthAmount);

            // For display (always positive with direction)
            const absolutePercentage = Math.abs(momPercentage);
            const direction = momPercentage > 0 ? 'increase' : (momPercentage < 0 ? 'decrease' : 'no change');

            setAvgDealSize({
                avgDealSize: dealSize,
                momPercentage: absolutePercentage.toFixed(2),
                direction: direction
            })

            const wonCurrMonthDeals = Number(res.data.total_won_deals || 0)
            const lostCurrMonthDeals = Number(res.data.total_lost_deals || 0)
            const lastMonthWonDeals = Number(res.data.total_won_last_month_deals || 0)
            const lastMonthLostDeals = Number(res.data.total_lost_last_month_deals || 0)

            const { winRatePercentChange, lossRatePercentChange } = calculateWinLossRate(wonCurrMonthDeals, lostCurrMonthDeals, lastMonthWonDeals, lastMonthLostDeals)

            setDealsWonRate({
                winRateChange: winRatePercentChange > 0 ? winRatePercentChange.toFixed(2) : 0,
                lossRateChange: lossRatePercentChange > 0 ? lossRatePercentChange.toFixed(2) : 0,
                currentMonthWonDeals: wonCurrMonthDeals,
                currentMonthLostDeals: lostCurrMonthDeals,
            })


        } catch (error) {
            return
        }
    }

    useEffect(() => {
        fetchRevenueData()
        fetchCardsData()
    }, [selectedPeriod])

    return (
        <Row className="g-3 mb-4">
            <Col sm={4} xl={3}>
                <Card className="shadow-sm rounded-3 p-0 w-100 h-100" style={{ backgroundColor: "#ffffff", border: "1px solid #e3e3e3" }}>
                    <Card.Body className="p-3">
                        <div className="d-flex align-items-center justify-content-between mb-2">
                            <span className="text-dark fw-medium" style={{ fontSize: "14.5px" }}>{revenueData.title}</span>
                            <Dropdown as="div" align={"end"} >
                                <Dropdown.Toggle
                                    as="button"
                                    className="btn btn-light d-flex align-items-center gap-2 px-2 py-1 text-muted no-arrow"
                                    style={{ fontSize: '12px', borderRadius: '8px', fontWeight: '600', border: '1px solid #efefef', textTransform: 'capitalize' }}
                                >
                                    <span className="text-capitalize">{selectedPeriod}</span>
                                    <i className="fa-solid fa-chevron-down align-middle ms-1 d-inline-block"></i>
                                </Dropdown.Toggle>
                                <Dropdown.Menu
                                    className="shadow"
                                    style={{
                                        width: "10rem",
                                        padding: "8px 0px",
                                        right: "3.4rem",
                                    }}
                                >
                                    {
                                        revenueDataPeriod?.map(c => (
                                            <Dropdown.Item
                                                href="#"
                                                className="d-flex align-items-center"
                                                onClick={() => setSelectedPeriod(c.value)}
                                            >
                                                {c.title}
                                            </Dropdown.Item>
                                        ))
                                    }
                                </Dropdown.Menu>
                            </Dropdown>
                        </div>

                        <div className='d-flex gap-2 align-items-baseline'>
                            <h3 className=" text-dark fw-normal mb-2" style={{ letterSpacing: "-0.02em" }}>
                                {`₹ ${putCommaInLeadsValue(revenueData.current)}`}
                            </h3>
                            <span
                                className={`border ${revenueData.isUp ? 'text-success ' : 'text-danger '} fw-bold px-0 border-0 py-1 d-flex align-items-center gap-1`}
                                style={{ fontSize: "12.5px" }}
                            >
                                {revenueData.isUp ? <IconTrendingUp size={12} fontWeight={600} /> : <IconTrendingDown size={12} fontWeight={600} />}
                                {revenueData.growth}
                            </span>
                        </div>

                        <div style={dividerStyle} />
                        <div className='d-flex justify-content-between align-items-end'>
                            <div className="pt-3 text-muted" style={{ fontSize: "12.5px" }}>
                                <span className="text-muted fw-medium d-inline-block mb-2" style={{ fontSize: "11.5px" }}>{revenueData.prevLabel}</span>
                                <h3 className=" mb-0 fw-normal" style={{ letterSpacing: "-0.02em", fontSize: "1.3rem", color: "#ff7300" }}>
                                    {`₹ ${putCommaInLeadsValue(revenueData.prev)}`}
                                </h3>
                            </div>
                            <div style={{ width: "100px", height: "45px" }}>
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={revenueData.chartData || sparklineData}>
                                        <defs>
                                            <linearGradient id={`gradient-${revenueData.isUp ? 'up' : 'down'}`} x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="0%" stopColor={revenueData.isUp ? '#16a34a' : '#dc2626'} stopOpacity={0.25} />
                                                <stop offset="100%" stopColor={revenueData.isUp ? '#16a34a' : '#dc2626'} stopOpacity={0.0} />
                                            </linearGradient>
                                        </defs>
                                        <Area
                                            type="monotone"
                                            dataKey="val"
                                            stroke={revenueData.isUp ? '#16a34a' : '#dc2626'}
                                            strokeWidth={1.8}
                                            fillOpacity={1}
                                            fill={`url(#gradient-${revenueData.isUp ? 'up' : 'down'})`}
                                            isAnimationActive={true}
                                        />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>

                        </div>
                    </Card.Body>
                </Card>
            </Col>

            <Col sm={4} xl={3}>
                <Card className="shadow-sm rounded-3 pb-0 w-100 h-100" style={{ backgroundColor: "#ffffff", border: "1px solid #e3e3e3" }}>
                    <Card.Body className="p-0">

                        <div className='pb-2'>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <span className="text-dark fw-medium" style={{ fontSize: "14.5px" }}>
                                    Won Deals
                                </span>
                                <span
                                    className="badge rounded-pill fw-semibold border"
                                    style={{
                                        backgroundColor: "#f0fdf4",
                                        color: "#15803d",
                                        borderColor: "#bbf7d0",
                                        fontSize: "10px"
                                    }}
                                >
                                    +{dealsWonRate?.winRateChange}% Win Rate
                                </span>
                            </div>
                            <div className="d-flex align-items-baseline gap-2">
                                <h4 className="mb-0 fw-normal" style={{ fontSize: '2rem' }}>
                                    {dealsWonRate?.currentMonthWonDeals}
                                </h4>
                                <span className="text-muted" style={{ fontSize: "11.5px" }}>
                                    closed opportunities
                                </span>
                            </div>
                        </div>

                        <div style={dividerStyle} />
                        <div className="pt-3 mb-0 pb-0 text-muted" style={{ fontSize: "12.5px" }}>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <span className="text-muted fw-medium" style={{ fontSize: "11.5px" }}>
                                    Lost Deals
                                </span>
                                <span
                                    className="badge rounded-pill fw-semibold border"
                                    style={{
                                        backgroundColor: "#fef2f2",
                                        color: "#b91c1c",
                                        borderColor: "#fecdd3",
                                        fontSize: "10px"
                                    }}
                                >
                                    {dealsWonRate?.lossRateChange}% Drop-off
                                </span>
                            </div>
                            <div className="d-flex align-items-baseline gap-2">
                                <h4 className=" fw-normal mb-0" style={{ fontSize: "2rem", color: "#828282" }}>
                                    {dealsWonRate?.currentMonthLostDeals}
                                </h4>
                                <span className="text-muted" style={{ fontSize: "11.5px" }}>
                                    lost opportunities
                                </span>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
            <Col sm={4} xl={3}>
                <Card className="shadow-sm rounded-3 p-0 w-100 h-100" style={{ backgroundColor: "#ffffff", border: "1px solid #e3e3e3" }}>
                    <Card.Body className="p-3 d-flex flex-column justify-content-between">
                        <div>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <span className="text-dark fw-medium mb-2" style={{ fontSize: "14.5px" }}>
                                    Monthly Recurring (MRR)
                                </span>
                                <span
                                    className="badge rounded-pill fw-semibold border"
                                    style={{ backgroundColor: "#faf5f8", color: "#7a3a5e", borderColor: "#e8d8e3", fontSize: "10px" }}
                                >
                                    ARR: ₹5.13 Cr
                                </span>
                            </div>
                            <div className="d-flex align-items-baseline gap-2">
                                <h3 className="fw-normal text-dark mb-0" style={{ letterSpacing: "-0.02em" }}>
                                    ₹42,80,000
                                </h3>
                                <span className="text-success fw-bold d-flex align-items-center gap-0.5" style={{ fontSize: "12px" }}>
                                    <IconTrendingUp size={12} fontWeight={600} />
                                    +8.4%
                                </span>
                            </div>
                        </div>

                        <div style={dividerStyle} />

                        <div className="pt-2 text-muted" style={{ fontSize: "12.5px" }}>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <h3 className="fw-normal mb-0" style={{ letterSpacing: "-0.02em", color: "#828282" }}>
                                    1.8%
                                </h3>
                                <span className="text-muted" style={{ fontSize: "11.5px" }}>
                                    Renewal Rate
                                </span>
                            </div>
                            <div className="d-flex align-items-baseline justify-content-between">
                                <span className="text-muted fw-medium" style={{ fontSize: "11.5px" }}>
                                    Monthly churn rate
                                </span>
                                <span
                                    className="badge rounded-pill fw-semibold border"
                                    style={{ backgroundColor: "#f0fdf4", color: "#15803d", borderColor: "#bbf7d0", fontSize: "10px" }}
                                >
                                    94.2% Retained
                                </span>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
            <Col sm={4} xl={3}>
                <Card className="shadow-sm rounded-3 p-0 w-100 h-100" style={{ backgroundColor: "#ffffff", border: "1px solid #e3e3e3" }}>
                    <Card.Body className="p-3 d-flex flex-column justify-content-between">
                        {/* TOP SECTION: AVERAGE DEAL SIZE */}
                        <div className="pb-2">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <span className="text-dark fw-medium mb-2" style={{ fontSize: "14.5px" }}>
                                    Avg Deal Size (ACV)
                                </span>
                                <span
                                    className="badge rounded-pill fw-semibold border"
                                    style={{ backgroundColor: "#faf5f8", color: "#7a3a5e", borderColor: "#e8d8e3", fontSize: "10px" }}
                                >
                                    {avgDealSize?.momPercentage > 0 && "+"}{avgDealSize?.momPercentage}% MoM
                                </span>
                            </div>
                            <div className="d-flex align-items-baseline gap-2">
                                <h3 className="fw-normal text-dark mb-0" style={{ letterSpacing: "-0.02em" }}>
                                    ₹{avgDealSize?.avgDealSize ? putCommaInLeadsValue(avgDealSize?.avgDealSize) : 0}
                                </h3>
                                <span className="text-muted" style={{ fontSize: "11.5px" }}>
                                    per contract
                                </span>
                            </div>
                        </div>

                        {/* BOTTOM SECTION: GROSS MARGIN */}
                        <div style={dividerStyle} />
                        <div className="pt-2 text-muted" style={{ fontSize: "12.5px" }}>
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <h3 className="fw-normal mb-0" style={{ letterSpacing: "-0.02em", color: "#828282" }}>
                                    78.5%
                                </h3>
                                <span className="text-muted" style={{ fontSize: "11.5px" }}>
                                    gross efficiency
                                </span>
                            </div>
                            <div className="d-flex align-items-baseline justify-content-between">
                                <span className="text-muted fw-medium" style={{ fontSize: "11.5px" }}>
                                    Gross Margin
                                </span>
                                <span
                                    className="badge rounded-pill fw-semibold border"
                                    style={{ backgroundColor: "#f0fdf4", color: "#15803d", borderColor: "#bbf7d0", fontSize: "10px" }}
                                >
                                    Optimal
                                </span>
                            </div>
                        </div>
                    </Card.Body>
                </Card>
            </Col>
        </Row>
    )
}
